import { useEffect } from "react";
import Animated, {
	interpolateColor,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from "react-native-reanimated";
import useStyle from "src/hooks/useStyle";
import IconCheck from "src/icons/IconCheck";
import { color } from "src/styles";
import { radiusExtraSmall } from "src/styles/radius";
import { ToggleBadgeProps } from ".";

const SIZE = 22;
const ToggleBadge = ({
	testID,
	isSelected,
	style,
	variant = "MultiSelect",
	colorVariant = "Selection",
	Icon,
}: ToggleBadgeProps) => {
	const { theme } = useStyle();

	const progressSV = useSharedValue(+isSelected);
	useEffect(() => {
		progressSV.value = withTiming(+isSelected, {
			duration: 200,
		});
	}, [progressSV, isSelected]);
	const containerAnimatedStyle = useAnimatedStyle(() => {
		switch (variant) {
			case "MultiSelect":
				return {
					backgroundColor: interpolateColor(
						progressSV.value,
						[0, 1],
						["rgba(255, 255, 255, 0.1)", color[colorVariant][theme]],
					),
					borderColor: interpolateColor(
						progressSV.value,
						[0, 1],
						[color.ButtonBorder[theme], color[colorVariant][theme]],
					),
				};
			case "SingleSelect":
				return {
					backgroundColor: interpolateColor(
						progressSV.value,
						[0, 1],
						["rgba(255, 255, 255, 0.1)", "rgba(0,0,0,0)"],
					),
					borderColor: interpolateColor(
						progressSV.value,
						[0, 1],
						[color.ButtonBorder[theme], color[colorVariant][theme]],
					),
				};
			default:
				return {};
		}
	}, [colorVariant, progressSV, theme, variant]);
	const filledContentAnimatedStyle = useAnimatedStyle(
		() => ({
			opacity: progressSV.value,
		}),
		[progressSV],
	);
	const emptyContentAnimatedStyle = useAnimatedStyle(
		() => ({
			opacity: 1 - progressSV.value,
		}),
		[progressSV],
	);

	return (
		<Animated.View
			testID={testID}
			style={[
				{
					borderWidth: 2,
					width: SIZE,
					height: SIZE,
					borderRadius: variant === "MultiSelect" ? radiusExtraSmall : SIZE / 2,
					backgroundColor: "transparent",
					justifyContent: "center",
					alignItems: "center",
				},
				containerAnimatedStyle,
				style,
			]}
		>
			{(() => {
				if (variant === "MultiSelect") {
					return (
						<>
							{!!Icon && (
								<Animated.View
									style={[
										{
											position: "absolute",
										},
										emptyContentAnimatedStyle,
									]}
								>
									<Icon
										size={SIZE / 2}
										color={
											colorVariant === "Selection"
												? color.TextContrast[theme]
												: color.ButtonBorder[theme]
										}
									/>
								</Animated.View>
							)}
							<Animated.View
								style={[
									{
										position: "absolute",
									},
									filledContentAnimatedStyle,
								]}
							>
								<IconCheck
									size={12}
									color={
										colorVariant === "Selection"
											? color.TextMatch[theme]
											: color.ButtonBorder[theme]
									}
								/>
							</Animated.View>
						</>
					);
				}
				if (variant === "SingleSelect") {
					return (
						<>
							{!!Icon && (
								<Animated.View
									style={[
										{
											position: "absolute",
										},
										emptyContentAnimatedStyle,
									]}
								>
									<Icon size={12} color={color.ButtonBorder[theme]} />
								</Animated.View>
							)}
							<Animated.View
								style={[
									{
										position: "absolute",
										width: SIZE - 8,
										height: SIZE - 8,
										backgroundColor: color[colorVariant][theme],
										borderRadius: SIZE / 2,
									},
									filledContentAnimatedStyle,
								]}
							/>
						</>
					);
				}
				return null;
			})()}
		</Animated.View>
	);
};
export default ToggleBadge;
